import { PanelCard } from '../../../components/ui';
import ColumnBars from '../../../components/charts/ColumnBars';
import type { AcademicDashboardResponse } from '../../../api/types';

const lowerBound = (bucket: string) => Number(bucket.split('-')[0]);

/**
 * The backend returns ten 10-point bands. The design draws eight bars: 0–20 and 20–40 are merged
 * pairs, then 10-point bands from 40 up.
 */
export function mergeDistribution(rows: { bucket: string; count: number }[]): { lower: number; count: number }[] {
  const out = new Map<number, number>();
  for (const r of rows) {
    const lo = lowerBound(r.bucket);
    const key = lo < 40 ? Math.floor(lo / 20) * 20 : lo;
    out.set(key, (out.get(key) ?? 0) + r.count);
  }
  return [...out.entries()].sort((a, b) => a[0] - b[0]).map(([lower, count]) => ({ lower, count }));
}

export default function DistributionCard({ d, className = '' }: { d: AcademicDashboardResponse; className?: string }) {
  const buckets = mergeDistribution(d.score_distribution).map((b) => ({ label: String(b.lower), value: b.count, highlight: b.lower < d.pass_score_pct, title: `${b.lower}%+: ${b.count} students` }));
  // The tiles come from the raw ten bands against the real pass mark, not from the merged bars.
  const below = d.score_distribution.filter((b) => lowerBound(b.bucket) < d.pass_score_pct).reduce((s, b) => s + b.count, 0);
  const pass = d.score_distribution.filter((b) => lowerBound(b.bucket) >= d.pass_score_pct).reduce((s, b) => s + b.count, 0);
  return (
    <PanelCard className={className} title="Score distribution" subtitle="Students by average score">
      <ColumnBars buckets={buckets} />
      <div className="mt-[18px] grid grid-cols-2 gap-2.5">
        <div className="rounded-[14px] bg-accent-bg p-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-accent-ink"><span className="h-2.5 w-2.5 rounded-[3px] bg-accent" />Below pass</div>
          <div className="mt-1 font-mono text-[22px] font-semibold text-ink">{below}</div>
        </div>
        <div className="rounded-[14px] bg-ground p-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-2"><span className="h-2.5 w-2.5 rounded-[3px] bg-slate" />Pass</div>
          <div className="mt-1 font-mono text-[22px] font-semibold text-ink">{pass}</div>
        </div>
      </div>
    </PanelCard>
  );
}
